import { NavLink, useParams } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Column,
    ProductType,
    productTypeConverter,
    Language,
    languageConverter,
    Country,
    countryConverter,
    Section,
    sectionConverter,
    clauseConverter,
    Clause,
    Coverage,
    coverageConverter,
    PRODUCT_TYPE_COLLECTION_NAME,
    LANGUAGE_COLLECTION_NAME,
    COUNTRY_COLLECTION_NAME,
    COVERAGE_COLLECTION_NAME,
    SUBSECTION_COLLECTION_NAME,
    subsectionConverter,
    SECTION_COLLECTION_NAME,
    CLAUSE_COLLECTION_NAME,
    TRANSLATIONS_COLLECTION_NAME,
    translationCoverter,
} from 'models';
import { CollectionList } from './recordList';

import './settings.css';
import { renderSectionList } from 'components/sectionList/sectionList';
import { CollectionProvider } from './collectionContext';

const optionClassname = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'option activeOption' : 'option';

const OptionArrow = () => <ArrowForwardIcon className="optionArrow" sx={{ fontSize: 14 }} />;

const Sidebar = () => (
    <div className="sidebar">
        <NavLink className={optionClassname} to="/settings/product-types">
            <OptionArrow />
            Product Types
        </NavLink>
        <NavLink className={optionClassname} to="/settings/languages">
            <OptionArrow />
            Languages
        </NavLink>
        <NavLink className={optionClassname} to="/settings/countries">
            <OptionArrow />
            Countries
        </NavLink>
        <NavLink className={optionClassname} to="/settings/sections">
            <OptionArrow />
            Sections
        </NavLink>
        <NavLink className={optionClassname} to="/settings/clauses">
            <OptionArrow />
            Clauses
        </NavLink>
        <NavLink className={optionClassname} to="/settings/coverage">
            <OptionArrow />
            Coverage
        </NavLink>
        <NavLink className={optionClassname} to="/settings/translations">
            <OptionArrow />
            Translations
        </NavLink>
    </div>
);

const productTypeColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Product type',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
    { field: 'description', label: 'Description', input: true },
];
const languageColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Language',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
];
const translationColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Language',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
    { field: 'completion', label: 'Completion', column: 'right' },
];
const countryColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Country',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
];
const coverageColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Coverage',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
    {
        field: 'productTypeIndexIds',
        label: 'Product Types',
        column: 'left',
        input: true,
        direction: 'asc',
        required: true,
        source: PRODUCT_TYPE_COLLECTION_NAME,
        fieldType: 'subset',
    },
    { field: 'content', label: 'Content', input: true, direction: 'asc' },
];
const sectionColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'asc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'Section',
        filter: true,
        index: 'indexName',
        column: 'right',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
    { field: 'description', label: 'Description', input: true },
    {
        field: 'coverage',
        label: 'Coverage section',
        input: true,
        fieldType: 'boolean',
    },
];

const clauseColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'reviewStatus',
        label: 'Review Status',
        fieldType: 'reviewStatus',
        column: 'left',
    },
    {
        field: 'name',
        label: 'Clause name',
        filter: true,
        index: 'indexName',
        column: 'left',
        input: true,
        selected: true,
        direction: 'asc',
        required: true,
    },
    {
        field: 'productTypeIndexIds',
        label: 'Product Types',
        column: 'left',
        input: true,
        required: true,
        source: PRODUCT_TYPE_COLLECTION_NAME,
        fieldType: 'subset',
    },
    {
        field: 'sectionIndexId',
        label: 'Section',
        column: 'left',
        input: true,
        required: true,
        source: SECTION_COLLECTION_NAME,
    },
    { field: 'content', label: 'Content', input: true },
];

const Main = () => {
    const { recordType } = useParams();
    return (
        <div className="main">
            <div className="title">Settings</div>
            {recordType === 'product-types' ? (
                <CollectionList
                    key="productType"
                    label="Product Type"
                    name="product type"
                    collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                    columns={productTypeColumns}
                    converter={productTypeConverter}
                    nextTo="/settings/languages"
                    historyTo="/settings/product-types/commits"
                    getNew={(data) => new ProductType(data)}
                />
            ) : recordType === 'languages' ? (
                <CollectionList
                    key="language"
                    label="Language"
                    name="language"
                    collectionName={LANGUAGE_COLLECTION_NAME}
                    columns={languageColumns}
                    converter={languageConverter}
                    nextTo="/settings/countries"
                    historyTo="/settings/languages/commits"
                    getNew={(data) => new Language(data)}
                />
            ) : recordType === 'countries' ? (
                <CollectionList
                    key="country"
                    label="Countries"
                    name="country"
                    collectionName={COUNTRY_COLLECTION_NAME}
                    columns={countryColumns}
                    converter={countryConverter}
                    nextTo="/settings/sections"
                    historyTo="/settings/countries/commits"
                    getNew={(data) => new Country(data)}
                />
            ) : recordType === 'sections' ? (
                <CollectionProvider
                    collectionName={SUBSECTION_COLLECTION_NAME}
                    converter={subsectionConverter}
                >
                    <CollectionList
                        key="section"
                        label="Sections"
                        name="section"
                        collectionName={SECTION_COLLECTION_NAME}
                        columns={sectionColumns}
                        converter={sectionConverter}
                        nextTo="/settings/clauses"
                        historyTo="/settings/sections/commits"
                        getNew={(data) => new Section(data)}
                        list={renderSectionList}
                    />
                </CollectionProvider>
            ) : recordType === 'clauses' ? (
                <CollectionProvider
                    collectionName={SECTION_COLLECTION_NAME}
                    converter={sectionConverter}
                >
                    <CollectionProvider
                        collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                        converter={productTypeConverter}
                    >
                        <CollectionList
                            key="clause"
                            label="Clauses"
                            name="clause"
                            collectionName={CLAUSE_COLLECTION_NAME}
                            columns={clauseColumns}
                            converter={clauseConverter}
                            nextTo="/settings/coverage"
                            historyTo="/settings/clauses/commits"
                            getNew={(data) => new Clause(data)}
                        />
                    </CollectionProvider>
                </CollectionProvider>
            ) : recordType === 'coverage' ? (
                <CollectionProvider
                    collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                    converter={productTypeConverter}
                >
                    <CollectionList
                        key="coverage"
                        label="Coverage"
                        name="coverage"
                        collectionName={COVERAGE_COLLECTION_NAME}
                        columns={coverageColumns}
                        converter={coverageConverter}
                        nextTo="/settings/translations"
                        historyTo="/settings/coverage/commits"
                        getNew={(data) => new Coverage(data)}
                    />
                </CollectionProvider>
            ) : recordType === 'translations' ? (
                <CollectionProvider
                    collectionName={SECTION_COLLECTION_NAME}
                    converter={sectionConverter}
                >
                    <CollectionProvider
                        collectionName={SUBSECTION_COLLECTION_NAME}
                        converter={subsectionConverter}
                    >
                        <CollectionProvider
                            collectionName={COVERAGE_COLLECTION_NAME}
                            converter={coverageConverter}
                        >
                            <CollectionProvider
                                collectionName={CLAUSE_COLLECTION_NAME}
                                converter={clauseConverter}
                            >
                                <CollectionProvider
                                    collectionName={TRANSLATIONS_COLLECTION_NAME}
                                    converter={translationCoverter}
                                    keyFunction={(translation) =>
                                        `${translation.languageIndexId}-${translation.targetId}`
                                    }
                                >
                                    <CollectionList
                                        key="translations"
                                        label="Language"
                                        name="language"
                                        collectionName={LANGUAGE_COLLECTION_NAME}
                                        columns={translationColumns}
                                        converter={languageConverter}
                                        getNew={(data) => new Language(data)}
                                    />
                                </CollectionProvider>
                            </CollectionProvider>
                        </CollectionProvider>
                    </CollectionProvider>
                </CollectionProvider>
            ) : null}
        </div>
    );
};

export const Settings = () => (
    <div className="page">
        <Sidebar />
        <Main />
    </div>
);
